import get from 'lodash/get';
import memoize from 'lodash/memoize';
import moment from 'moment';
import { blue, purple, cyan, geekblue } from '@ant-design/colors';

import { DATEPICKER_ALLTIME_ID } from 'components/HirerComponents/ReviewReporting/constants';
import { RangeType, RangeTypeId, SelectedDateRange } from 'types';

const colors = [blue, purple, cyan, geekblue];

export function getError(error: unknown | string): string {
  let errorString = '';
  if (typeof error === 'string') {
    errorString = error;
  } else if (typeof error === 'object') {
    errorString = get(error, 'message') || '';
  }

  return errorString.replace('GraphQL error: ', '');
}

export function getNetworkError(error: unknown | string): string {
  if (!error) {
    return '';
  }
  let errorString = '';
  if (typeof error === 'string') {
    errorString = error;
  } else if (typeof error === 'object') {
    errorString = get(error, 'networkError.result.errors.0.message') || getError(error);
  }

  if (typeof errorString === 'string') {
    return errorString.replace('GraphQL error: ', '');
  }

  return '';
}

export const sortByKey = memoize((arr: unknown[], key: string): unknown[] =>
  arr.sort((a, b) => {
    const keyA = get(a, key, '');
    const keyB = get(b, key, '');
    const nameA = typeof keyA === 'string' ? keyA.toUpperCase() : keyA;
    const nameB = typeof keyB === 'string' ? keyB.toUpperCase() : keyB;

    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }

    return 0;
  })
);

export function getDatesFromRangeType(
  selectedRangeType: RangeType,
  selectedDateRange: SelectedDateRange
): SelectedDateRange {
  const startDate =
    selectedRangeType.id === DATEPICKER_ALLTIME_ID ? null : selectedDateRange.startDate;
  const endDate = selectedRangeType.id === DATEPICKER_ALLTIME_ID ? null : selectedDateRange.endDate;

  return { startDate, endDate };
}

export function getDateRange(type: RangeTypeId): SelectedDateRange {
  const today = moment();
  return {
    endDate: today.toDate(),
    startDate: moment(today)
      .subtract(1, type === 'custom' || type === 'all' ? 'month' : type)
      .toDate(),
  };
}

export function selectDates(
  rangeType: RangeTypeId,
  selectedDateRange: SelectedDateRange
): SelectedDateRange {
  let dates = { startDate: null, endDate: null } as SelectedDateRange;
  switch (rangeType) {
    case 'all': {
      dates = getDateRange(rangeType);
      break;
    }
    case 'custom': {
      dates = selectedDateRange;
      break;
    }
    case 'week':
    case 'month':
    default: {
      dates = getDateRange(rangeType);
      break;
    }
  }
  return dates;
}

export function questionComponentIdentifierMatches(
  componentIdentifiers: string[],
  questionComponentIdentifier: string
): boolean {
  for (let i = 0; i < componentIdentifiers.length; i++) {
    const componentIdentifier = componentIdentifiers[i];
    if (questionComponentIdentifier.includes((componentIdentifier || '').toLowerCase())) {
      return true;
    }
  }
  return false;
}

export function getHasuraOrderBy<T>(sortField: Array<string> | string, sortDirection: string): T {
  if (typeof sortField === 'string') {
    return {
      [sortField]: sortDirection,
    } as unknown as T;
  }
  const orderBy = {} as Record<string, unknown>;
  sortField.reduce((acc, value, idx) => {
    const next = {} as Record<string, unknown>;
    if (idx === sortField.length - 1) {
      acc[value] = sortDirection;
      return orderBy;
    }
    acc[value] = next;

    return next;
  }, orderBy);

  return orderBy as unknown as T;
}

function _iterate<T, K>(
  offset: number,
  array: Array<T>,
  limit: number,
  count: number,
  cb: (obj: T) => Promise<K>
): Promise<Array<K>> {
  const lastIndex = Math.min((offset + 1) * limit, count);
  const toUpdate = array.slice(offset * limit, lastIndex);
  const promises = toUpdate.map(obj => cb(obj));

  return Promise.all(promises);
}

export async function resolvePromiseArrayInChunks<T, K>(
  array: Array<T>,
  limit: number,
  cb: (answer: T) => Promise<K>
): Promise<Array<K>> {
  const count = array.length;

  const iterators: Array<number> = [];
  for (let i = 0; i < Math.ceil(count / limit); i += 1) {
    iterators.push(i);
  }

  const resolvedPromises = iterators.reduce(
    (promiseChain, currentTask: number) =>
      promiseChain.then(chainResults =>
        _iterate<T, K>(currentTask, array, limit, count, cb).then(currentResult =>
          chainResults.concat(currentResult)
        )
      ),
    // add a stub promise to start the promise chain
    Promise.resolve([] as Array<K>)
  );

  return resolvedPromises;
}

export function getRandomColor(id: number): string {
  const color = colors[id % colors.length];

  return color[1 + (id % 6)];
}
